import { useState, useRef, useEffect } from "react";
import SearchIcon from "../../../../assets/icons/search-icon.svg";
import closeButton from "../../../../assets/icons/CPCloseIcon.svg";
import "./AccessibleAutocompleteDropdown.css"; // Assuming you have a CSS file for styles
import PropTypes from "prop-types";

const AccessibleAutocompleteDropdown = ({
  id,
  options,
  label,
  placeholder,
  selectedOption,
  disabled = false,
  required = false,
  className = "",
  width = "100%",
  onSelect,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [filteredOptions, setFilteredOptions] = useState([]);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [selectedIndex, setSelectedIndex] = useState(-1);

  const dropdownRef = useRef(null);
  const inputRef = useRef(null);
  const listboxRef = useRef(null);
  const lastInteraction = useRef(null);
  const isTalkBack = useRef(false);
  const ignoreFocus = useRef(false);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setInputValue("");
        setHighlightedIndex(0);
      }
    };
    const dropdownElement = dropdownRef.current;
    const keydown = () => {
      lastInteraction.current = "KEYBOARD";
    };
    const mousedown = () => {
      lastInteraction.current = "MOUSE";
    };
    const touchdown = () => {
      lastInteraction.current = "TOUCH";
    };
    document.addEventListener("keydown", keydown);
    dropdownElement.addEventListener("mousedown", mousedown);
    dropdownElement.addEventListener("touchstart", touchdown);
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("keydown", keydown);
      dropdownElement.removeEventListener("mousedown", mousedown);
      dropdownElement.removeEventListener("touchstart", touchdown);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    setFilteredOptions(options?.length ? [...options] : []);
  }, [options]);

  useEffect(() => {
    if (isOpen && listboxRef.current) {
      scrollIntoViewHandler(highlightedIndex);
    }
  }, [isOpen, highlightedIndex]);

  const scrollIntoViewHandler = (index, block = "nearest") => {
    const highlightedElement = listboxRef.current?.children[index];
    if (highlightedElement) {
      highlightedElement.scrollIntoView({ block });
    }
  };

  const inputChangeHandler = (event) => {
    let value = event.target.value?.trim();
    if (selectedOption) {
      value = value.split(selectedOption.label)[0];
    }
    setInputValue(value);
    let mainIndex = selectedIndex,
      filteredOptions = [...options];
    if (value && value.length > 2) {
      filteredOptions = options.filter((option) => option.label.toLowerCase().includes(value.toLowerCase()));
    } else if (!value) {
      setTimeout(() => {
        inputRef.current.scrollLeft = 0;
        inputRef.current.setSelectionRange(0, 0);
      }, 0);
    }
    if (selectedOption) {
      mainIndex = filteredOptions.findIndex((opt) => opt.label === selectedOption?.label);
      setSelectedIndex(mainIndex);
    }
    setHighlightedIndex(0);
    setFilteredOptions([...filteredOptions]);
    setIsOpen(true);
    setTimeout(() => {
      mainIndex >= 0 ? scrollIntoViewHandler(mainIndex, "center") : scrollIntoViewHandler(0);
    }, 0);
  };

  function getNextHighlightedIndex(prev, filteredOptions, options) {
    if (prev < filteredOptions.length - 1) {
      return prev + 1;
    }
    if (prev === options.length - 1) {
      return 0;
    }
    return prev;
  };

  function getPrevHighlightedIndex(prev, options) {
    if (prev > 0) {
      return prev - 1;
    }
    if (prev === 0) {
      return options.length - 1;
    }
    return prev;
  };

  const inputKeyDownHandler = (event) => {
    if (!isOpen) {
      if (event.key === "ArrowDown" || event.key === "Enter" || event.key === " " || event.key === "touchstart") {
        let mainIndex = selectedIndex;
        if (mainIndex === -1 && selectedOption) {
          mainIndex = options.findIndex((opt) => opt.label === selectedOption?.label);
          setSelectedIndex(mainIndex);
        }
        setHighlightedIndex((prev) => (mainIndex >= 0 ? -1 : prev));
        event.preventDefault();
        event.stopPropagation();
        setIsOpen(true);
        setTimeout(() => {
          mainIndex >= 0 ? scrollIntoViewHandler(mainIndex, "center") : scrollIntoViewHandler(0);
        }, 0);
      }
      return;
    }

    switch (event.key) {
      case "Escape":
      case "Tab":
        event.preventDefault();
        event.stopPropagation();
        setIsOpen(false);
        setHighlightedIndex(0);
        setInputValue("");
        setTimeout(() => {
          inputRef.current.scrollLeft = 0;
          inputRef.current.setSelectionRange(0, 0);
        }, 0);

        break;
      case "ArrowDown":
        setHighlightedIndex((prev) => getNextHighlightedIndex(prev, filteredOptions, options));
        break;
      case "ArrowUp":
        setHighlightedIndex((prev) => getPrevHighlightedIndex(prev, options));
        break;
      case "Enter":
      case " ":
        event.preventDefault();
        if (highlightedIndex >= 0 && filteredOptions[highlightedIndex]) {
          optionClickHandler(event, filteredOptions[highlightedIndex], highlightedIndex);
        }
        break;
      default:
        break;
    }
  };

  const inputFocusHandler = (event) => {
    event.preventDefault();
    if (lastInteraction.current === "TOUCH" || lastInteraction.current === null || isTalkBack.current) {
      if (lastInteraction.current === null) {
        isTalkBack.current = true;
      }
      if (ignoreFocus.current) {
        ignoreFocus.current = false;
      } else if (!isOpen) {
        setIsOpen(true);
        let mainIndex = selectedIndex;
        if (mainIndex === -1 && selectedOption) {
          mainIndex = options.findIndex((opt) => opt.label === selectedOption?.label);
          setSelectedIndex(mainIndex);
        }
        setTimeout(() => {
          mainIndex >= 0 ? scrollIntoViewHandler(mainIndex, "center") : scrollIntoViewHandler(0);
        }, 0);
      }
    }
  };

  const inputClickHandler = (event) => {
    if (isOpen) {
      setIsOpen(false);
    } else {
      setIsOpen(true);
      let mainIndex = selectedIndex;
      if (mainIndex === -1 && selectedOption) {
        mainIndex = options.findIndex((opt) => opt.label === selectedOption?.label);
        setSelectedIndex(mainIndex);
      }
      setHighlightedIndex((prev) => (mainIndex >= 0 ? -1 : prev));
      setTimeout(() => {
        inputRef.current.scrollLeft = 0;
        inputRef.current.setSelectionRange(0, 0);
        mainIndex >= 0 ? scrollIntoViewHandler(mainIndex, "center") : scrollIntoViewHandler(0);
      }, 0);
    }
    inputRef.current.focus();
    event.preventDefault();
    event.stopPropagation();
  };

  const inputBlurHandler = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setFilteredOptions([...options]);
  };

  const clearHandler = (event) => {
    event.stopPropagation();
    event.preventDefault();
    setInputValue("");
    setFilteredOptions([...options]);
    setSelectedIndex(-1);
    setHighlightedIndex(0);
    setIsOpen(false);
    onSelect(null);
    if (isTalkBack.current) {
      ignoreFocus.current = true;
    }
    inputRef.current.focus();
  };

  const optionClickHandler = (event, option, index) => {
    onSelect(option);
    setInputValue("");
    setFilteredOptions([...options]);
    const mainIndex = options.findIndex((opt) => opt.label === option.label);
    setSelectedIndex(mainIndex);
    setHighlightedIndex(0);
    setTimeout(() => {
      inputRef.current.focus();
      inputRef.current.scrollLeft = 0;
      inputRef.current.setSelectionRange(0, 0);
    }, 0);

    if (isTalkBack.current) {
      ignoreFocus.current = true;
    }
    setIsOpen(false);
    event.stopPropagation();
    event.preventDefault();
  };

  const getOptionProps = (option, index) => {
    return {
      id: `${id}-option-${option.value}`,
      role: "option",
      "aria-selected": selectedOption?.value === option.value,
      tabIndex: 0,
      className: `autocomplete-option ${highlightedIndex === index ? "highlighted" : ""} ${selectedIndex === index ? "selected" : ""}`,
      onClick: (e) => optionClickHandler(e, option, index),
      onMouseEnter: () => setHighlightedIndex(index),
      onMouseDown: (e) => optionClickHandler(e, option, index),
      "aria-label": option.label,
    };
  };

  function renderDropdownOptions(isOpen, filteredOptions, inputValue, listboxRef, id, label, getOptionProps) {
    if (!isOpen) {
      return null;
    }
    if (filteredOptions.length === 0) {
      return <div className="no-options">No results found</div>;
    }
    if (inputValue.length === 1 || inputValue.length === 2) {
      return <div className="no-options">Enter at least 3 characters to search</div>;
    }
    return (
      <ul className="dropdown-options" ref={listboxRef} role="listbox" id={`${id}-listbox`} aria-label={label || "Options"}>
        {filteredOptions.map((option, index) => (
          <li key={option.label} {...getOptionProps(option, index)}>
            {option.label}
          </li>
        ))}
      </ul>
    );
  };

  return (
    <div className={`accessible-dropdown-container ${className}`} ref={dropdownRef} style={{ width }}>
      {label && (
        <label htmlFor={id} className={`dropdown-label ${required ? "required" : ""}`}>
          {label}
          {required && <span className="required-asterisk">*</span>}
          {/* <span className="sr-only" aria-hidden="false">
            Dropdown field. Tap to expand options.
          </span> */}
        </label>
      )}

      <div className="dropdown-wrapper">
        <img
          aria-hidden="true"
          aria-label="Search Icon"
          alt="search-icon"
          src={SearchIcon}
          style={{ width: "1rem", height: "1rem", margin: "0.875rem 0rem 0.875rem 1rem" }}
        />

        <input
          id={id}
          ref={inputRef}
          type="text"
          className="dropdown-input"
          value={inputValue !== "" ? inputValue : selectedOption?.label || ""}
          onChange={inputChangeHandler}
          onKeyDown={inputKeyDownHandler}
          onFocus={inputFocusHandler}
          onClick={inputClickHandler}
          onBlur={inputBlurHandler}
          placeholder={placeholder}
          aria-expanded={isOpen}
          aria-autocomplete="list"
          aria-controls={`${id}-listbox`}
          aria-activedescendant={
            highlightedIndex >= 0 && filteredOptions[highlightedIndex] ? `${id}-option-${filteredOptions[highlightedIndex].value}` : undefined
          }
          disabled={disabled}
          aria-required={required}
          role="combobox"
        />
        {selectedOption?.label && !inputValue ? (
          <button type="button" className="dropdown-toggle-button" onClick={clearHandler} tabIndex={-1} aria-label="Clear input" disabled={disabled}>
            <img src={closeButton} style={{ width: "1rem", height: "1rem", marginRight: "1rem" }} id="clear-icon" alt="Clear input" />
          </button>
        ) : (
          <></>
        )}
      </div>
      {renderDropdownOptions(isOpen, filteredOptions, inputValue, listboxRef, id, label, getOptionProps)}
    </div>
  );
};

AccessibleAutocompleteDropdown.propTypes = {
  id: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.any.isRequired,
    })
  ).isRequired,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  selectedOption: PropTypes.shape({
    label: PropTypes.string,
    value: PropTypes.any,
  }),
  disabled: PropTypes.bool,
  required: PropTypes.bool,
  className: PropTypes.string,
  width: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
};

export default AccessibleAutocompleteDropdown;